import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * SearchableDropdown — minimal Q Drives-styled dropdown.
 *
 * Why a fresh component (not Shadcn Combobox):
 *   The Sell flow runs without auth and needs to render fast on mobile.
 *   Shadcn Popover + Command bundles cmdk + radix-popover (~30kb gz) just
 *   to do exact + partial + case-insensitive matching that we can do in
 *   ~80 lines. Also keeps the dark/red Q Drives palette consistent.
 *
 * Props:
 *   options       string[] | { value, label, hint }[]
 *   value         current selected value
 *   onChange      (value) => void
 *   placeholder   default "Select…"
 *   testid        data-testid prefix (component adds "<prefix>-trigger" etc.)
 *   disabled      when parent dependency isn't ready yet
 *   normalize     custom matcher; receives (option_label_lower, q_lower)
 *
 * The trigger is a button; the menu is a position-absolute div. We close
 * on outside click and on Escape.
 */
export default function SearchableDropdown({
  options = [],
  value = "",
  onChange,
  placeholder = "Select…",
  testid,
  disabled = false,
  emptyLabel = "No matches",
  normalize,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Normalize option shapes — accept strings OR objects.
  const items = useMemo(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label || o.value, hint: o.hint }
      ),
    [options]
  );

  // Outside-click + Escape handlers.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Focus the search input as soon as the menu opens — mobile keyboard pop-up
  // is intentional; the dropdown is searchable by spec.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const haystack = (it.label || "").toLowerCase();
      if (normalize) return normalize(haystack, q);
      // Default: case-insensitive substring on the label. Also try a
      // hyphen-stripped variant so "S Cross" matches "S-Cross".
      const stripped = haystack.replace(/[-/\s]+/g, " ");
      return haystack.includes(q) || stripped.includes(q.replace(/[-/\s]+/g, " "));
    });
  }, [items, query, normalize]);

  const selected = items.find((it) => it.value === value);
  const triggerLabel = selected ? selected.label : placeholder;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        data-testid={testid ? `${testid}-trigger` : undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`qd-input-block bg-[#0E0E0E] cursor-pointer appearance-none pr-7 text-left flex items-center justify-between w-full ${
          disabled ? "opacity-40 cursor-not-allowed" : ""
        } ${selected ? "text-white" : "text-white/40"}`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%23999' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0.5rem center",
          backgroundSize: "12px 12px",
        }}
      >
        <span className="truncate">{triggerLabel}</span>
      </button>

      {open && (
        <div
          data-testid={testid ? `${testid}-menu` : undefined}
          className="absolute z-[60] mt-1 left-0 right-0 bg-[#0E0E0E] border border-[#2A2A2A] max-h-[60vh] overflow-y-auto"
        >
          <div className="p-2 border-b border-[#1A1A1A] sticky top-0 bg-[#0E0E0E]">
            <input
              ref={inputRef}
              data-testid={testid ? `${testid}-search` : undefined}
              className="qd-input-block bg-[#1A1A1A] text-white text-sm"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-xs text-white/40 font-mono-data tracking-wide">
              {emptyLabel}
            </div>
          ) : (
            <ul className="py-1">
              {filtered.map((it) => {
                const isActive = it.value === value;
                return (
                  <li key={it.value}>
                    <button
                      type="button"
                      data-testid={testid ? `${testid}-option-${it.value}` : undefined}
                      onClick={() => { onChange(it.value); setOpen(false); setQuery(""); }}
                      className={`w-full text-left px-3 py-2 text-sm font-display normal-case transition-colors ${
                        isActive
                          ? "bg-[#FF1E1E]/15 text-[#FF1E1E]"
                          : "text-white/85 hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="leading-tight">{it.label}</div>
                      {it.hint && <div className="text-[10px] font-mono-data text-white/35">{it.hint}</div>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
